/**
 * Status, filter, and display config for specialties.
 */
import { CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a specialty status. */
export interface SpecialtyStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Specialty list filter state. */
export interface SpecialtyFilters {
  statuses: string[];
  searchTerm?: string;
}

/** Filter group IDs for the specialties {@link DataTable} configure panel. */
export type FilterGroupId = 'filter-status' | 'filter-search';

/** Badge config for each specialty status. */
export const SPECIALTY_STATUS_CONFIG: Record<string, SpecialtyStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  inactive: {
    label: 'Inactif',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Default specialty list filters. */
export const DEFAULT_SPECIALTY_FILTERS: SpecialtyFilters = {
  statuses: [],
  searchTerm: undefined,
};

/** Configure-panel order for specialty filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = ['filter-status', 'filter-search'];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-status': 'Statut',
  'filter-search': 'Recherche',
};

/** Returns the badge color name for a specialty based on its active state. */
export function getSpecialtyStatusColor(isActive: boolean): 'success' | 'error' {
  return isActive ? 'success' : 'error';
}

/** React Query keys invalidated after a specialty create / save. */
export const SPECIALTY_FORM_QUERY_KEYS = {
  list: ['specialties'] as const,
  all: ['specialties', 'all'] as const,
  one: (id: string) => ['specialty', id] as const,
  highSchoolLevels: ['specialties', 'levels', 'high'] as const,
};
